import React, { useState } from 'react';
import {
  withStyles,
  Theme,
  createStyles,
  makeStyles,
} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import {
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  ExpandLess,
  ExpandMore,
} from '@material-ui/icons';
import {
  Box,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  getImageUrl,
  getViewCountInDifferentFormat,
} from '../../../Utils/commonUtils';
import useManagedHomeFeedActions from '../../../customHooks/ManagedHomeFeeds/useManagedHomeFeedActions';
import useManagedHomeFeedStore from '../../../Store/useManagedHomeFeedStore';
import useSectionVideos from '../../../customHooks/ManagedHomeFeeds/useSectionVideos';
import { OTT_LINK } from '../../../API/constants';
import moment from 'moment';

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    body: {
      fontSize: 14,
    },
  }),
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  }),
)(TableRow);

const useStyles = makeStyles({
  table: {
    minWidth: 700,
  },
  popover: {
    pointerEvents: 'none',
  },
  paper: {},
});

export default function VideosTable() {
  const { videos } = useSectionVideos();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const {
    handleReorderEntitiesInSection,
    handleDeleteEntityInSection,
  } = useManagedHomeFeedActions();
  const { selectedSectionKey, managedHomeFeed } = useManagedHomeFeedStore();
  const [hoveredImg, setHoveredImg] = useState('');

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const onMove = (currentIndex: number, id: string, targetIndex: number) => {
    if (!managedHomeFeed) return;

    const { ordered_entity_ids } = managedHomeFeed.section_data[
      selectedSectionKey
    ];

    const newOrderedIdsList = [...ordered_entity_ids];
    const [removed] = newOrderedIdsList.splice(currentIndex, 1);
    newOrderedIdsList.splice(targetIndex, 0, removed);

    handleReorderEntitiesInSection({
      sectionKey: selectedSectionKey,
      entityIds: newOrderedIdsList,
    });
  };

  const onDelete = (entityId: string) => {
    handleDeleteEntityInSection({ sectionKey: selectedSectionKey, entityId });
  };

  return (
    <TableContainer component={Paper}>
      <Table className={classes.table} aria-label="customized table">
        <TableHead>
          <TableRow>
            <StyledTableCell>Actions</StyledTableCell>
            <StyledTableCell align="left">Title</StyledTableCell>
            <StyledTableCell align="right">Views</StyledTableCell>
            <StyledTableCell align="right">Date & Time</StyledTableCell>
            <StyledTableCell align="right">Sport</StyledTableCell>
            <StyledTableCell align="right">Type</StyledTableCell>
            <StyledTableCell align="right">Channel Name</StyledTableCell>
            <StyledTableCell align="center">Deleted?</StyledTableCell>
            <StyledTableCell align="right">VIDEO LINK</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {videos.map((row, mainIndex) => (
            <StyledTableRow key={row._id}>
              <StyledTableCell component="th" scope="row">
                <Box display="flex" alignItems="center">
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ minWidth: 60 }}
                  >
                    {mainIndex !== 0 && (
                      <Tooltip title="Move Up">
                        <IconButton
                          size="small"
                          onClick={() =>
                            onMove(mainIndex, row._id, mainIndex - 1)
                          }
                          style={{ color: 'white' }}
                        >
                          <ExpandLess />
                        </IconButton>
                      </Tooltip>
                    )}
                    {mainIndex !== videos.length - 1 && (
                      <Tooltip title="Move Down">
                        <IconButton
                          size="small"
                          onClick={() =>
                            onMove(mainIndex, row._id, mainIndex + 1)
                          }
                          style={{ color: 'white' }}
                        >
                          <ExpandMore />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <IconButton onClick={() => onDelete(row._id)}>
                    <DeleteIcon />
                  </IconButton>{' '}
                  <IconButton>
                    <EditIcon />
                  </IconButton>
                </Box>
              </StyledTableCell>
              <StyledTableCell component="th" scope="row">
                <Box
                  aria-owns={open ? 'mouse-over-popover' : undefined}
                  aria-haspopup="true"
                  onMouseEnter={e => {
                    handlePopoverOpen(e);
                    setHoveredImg(getImageUrl(row.thumbnail));
                  }}
                  onMouseLeave={handlePopoverClose}
                  display="flex"
                  alignItems="flex-start"
                  style={{ gap: 10 }}
                >
                  <img
                    src={getImageUrl(row.thumbnail)}
                    style={{ width: 70, aspectRatio: '16/9' }}
                    alt="thumbnail"
                  />
                  <Typography variant="subtitle1"> {row.title}</Typography>
                </Box>
              </StyledTableCell>
              <StyledTableCell align="right">
                {getViewCountInDifferentFormat(row.views)}
              </StyledTableCell>

              <StyledTableCell align="right">
                {(row.article_type === 'youtube' ||
                  row.article_type === 'video') &&
                  row.created_on &&
                  moment(new Date(row.created_on))
                    .format('LLL')
                    .replace(',', '')}

                {row.article_type === 'stream' &&
                  row.start_timestamp &&
                  moment(new Date(row.start_timestamp))
                    .format('LLL')
                    .replace(',', '')}
              </StyledTableCell>
              <StyledTableCell align="right">{row.category}</StyledTableCell>
              <StyledTableCell align="right">
                {row.article_type}
              </StyledTableCell>
              <StyledTableCell align="right">{row.channel}</StyledTableCell>
              <StyledTableCell align="center">
                {row.is_active === 'Y' && (
                  <span style={{ color: 'green', fontWeight: 'bold' }}>NO</span>
                )}
                {row.is_active === 'N' && (
                  <span style={{ color: 'red', fontWeight: 'bold' }}>YES</span>
                )}
              </StyledTableCell>
              <StyledTableCell align="right">
                {(row.article_type === 'youtube' ||
                  row.article_type === 'video') && (
                  <a target="_blank" href={`${OTT_LINK}/video/${row._id}`}>
                    VIEW ON OTT
                  </a>
                )}
                {row.article_type === 'stream' && (
                  <a target="_blank" href={`${OTT_LINK}/stream/${row._id}`}>
                    VIEW ON OTT
                  </a>
                )}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>

      <Popover
        id="mouse-over-popover"
        className={classes.popover}
        classes={{
          paper: classes.paper,
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <img
          src={hoveredImg}
          style={{ width: 300, aspectRatio: '16/9' }}
          alt="thumbnail"
        />
      </Popover>
    </TableContainer>
  );
}
